import { useMemo } from 'react';
import { CanvasEdge, CanvasStandaloneNode } from '../../sweep-canvas/canvas-types';
import { SfImgIcon } from '../../common/sf-icons-dialog/SfImgIcon';
import { SweepCanvasReactFlowEdgeDataType } from '../../sweep-canvas/edges/SweepCanvasReactFlowEdgeDataType';
import { FunnelType } from '../../../types/enums/FunnelType';
import { useGetTopTag } from './useGetTopTag';
import { VisibilityMap } from '../../../types/VisibilityTypes';
import { translateTypeToHumanName } from '../../documentation/helpers';
import { CrmOrg } from '../../../types/CrmOrgs';
import { FunnelMapMetadataElement } from '../../../types/FunnelMapMetadataElement';

// Filters metadata elements:
//  - filters by elements not present in funnelMap.metadataElementsData
//  - filters by elements with missing data. The contract is if they are missing the element was possibly removed from SF.
//  - If the org no longer exists it means the element should not be rendered.

const filterInvalidMetadataElements =
  (funnelMap: FunnelMap, crmOrgs?: CrmOrg[]) =>
  ([id, { crmOrgId, metadataId }]: [string, FunnelMapMetadataElement]) => {
    const metadataElementData = funnelMap.metadataElementsData[id];
    if (!metadataElementData) {
      return false;
    }
    const { type, name } = metadataElementData;
    if (!(type && crmOrgId && name && metadataId)) {
      return false;
    }
    return crmOrgs?.some((crmOrg) => crmOrg.id === crmOrgId) || false;
  };

export const useCalculateMetadataElementStandaloneNodes = ({
  funnelMap,
  crmOrgs,
  visibilityMap,
}: {
  funnelMap: FunnelMap;
  crmOrgs?: CrmOrg[];
  visibilityMap: VisibilityMap;
}) => {
  const getTopTag = useGetTopTag(visibilityMap, crmOrgs);
  return useMemo(() => {
    const edges: CanvasEdge[] = [];
    const standaloneNodes: CanvasStandaloneNode[] = Object.entries(funnelMap.metadataElements || {})
      .filter(filterInvalidMetadataElements(funnelMap, crmOrgs))
      .map(([id, { position, icon, color, funnelLinks, crmOrgId, sectionId }]) => {
        const metadataElementData = funnelMap.metadataElementsData[id];
        const crmOrgName = getTopTag(crmOrgId);

        funnelLinks.forEach((link) => {
          if (link.stageId) {
            edges.push({
              id: `${id}-${link.stageId}`,
              source: id,
              target: link.stageId,
              data: { type: SweepCanvasReactFlowEdgeDataType.REMOVABLE, groupConnection: true },
            });
          } else {
            const targetPrefix = link._type === FunnelType.METADATA_ELEMENT ? '' : 'label-';
            const target = `${targetPrefix}${link.funnelId}`;
            edges.push({
              id: `${id}-${target}`,
              source: id,
              target: target,
              data: { type: SweepCanvasReactFlowEdgeDataType.REMOVABLE, groupConnection: true },
            });
          }
        });

        return {
          id,
          nodeType: 'standalone',
          name: metadataElementData.name,
          type: translateTypeToHumanName(metadataElementData.type),
          topTag: crmOrgName,
          position,
          icon: icon ? <SfImgIcon imageName={icon} width="16px" height="16px" /> : undefined,
          color,
          sectionId,
        };
      });
    return { standaloneNodes, edges };
  }, [funnelMap, crmOrgs, getTopTag]);
};
